import { mdiCalendarMonthOutline, mdiClockOutline, mdiCurrencyUsd, mdiDoor } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Button } from "@base/ui/Button/Button";
import { Chip } from "@base/ui/Chip";
import type { ReservationStatus } from "@features/reservation/model/types";
import { formatTimeRangeInLA } from "@features/reservation/lib/laTime";
import { STATUS_CHIP_MAP } from "@features/myinfo/config/constants";
import { useOverlay } from "@base/model/hooks/useOverlay";
import DeleteReservationDialog from "./DeleteReservationDialog";

function formatDate(dateStr: string): string {
  return dayjs(dateStr).format("MMM D, YYYY");
}

function formatRoomDisplay(roomName: string, t: (key: string) => string): string {
  const match = roomName.match(/^Room\s+(.+)$/i);
  if (match) {
    return `${t("room")} ${match[1].trim()}`;
  }
  return roomName;
}

export interface ReservationItemProps {
  id: number;
  title: string;
  date: string;
  room_name: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: ReservationStatus;
  is_paid: boolean;
  is_expired: boolean;
}

export function ReservationItem({
  id,
  title,
  date,
  room_name: roomName,
  start_time: startTime,
  end_time: endTime,
  duration_minutes: durationMinutes,
  status,
  is_paid: isPaid,
  is_expired: isExpired,
}: ReservationItemProps) {
  const { t } = useTranslation();
  const { open } = useOverlay();

  const { labelKey, color } = STATUS_CHIP_MAP[status];
  const hours = durationMinutes / 60;
  const paymentLabel = isPaid ? t("paid") : t("free");

  const handleOpenDeleteReservationDialog = () => {
    open(({ isOpen, close }) => <DeleteReservationDialog isOpen={isOpen} onClose={close} reservationId={id} />);
  };

  return (
    <div className="p-4 bg-white border border-gray-300 rounded-6">
      <div className="flex flex-row justify-between items-center">
        <Chip size="sm" color={color} variant="fill">
          {t(labelKey)}
        </Chip>
        {status !== "CANCELLED" && !isExpired && (
          <Button variant="outline" color="secondary" size="small" onClick={handleOpenDeleteReservationDialog}>
            {t("cancel")}
          </Button>
        )}
      </div>

      <h3 className="mt-4 text-gray-900 text-subtitle4 leading-[150%] tracking-[-0.32px]">{title}</h3>
      <div className="flex flex-col gap-3 mt-4">
        <div className="flex gap-2 items-center text-gray-800 text-base font-normal leading-[130%] tracking-[-0.32px]">
          <Icon path={mdiCalendarMonthOutline} size="1.25rem" className="text-gray-800 shrink-0" />
          <span>{formatDate(date)}</span>
        </div>
        <div className="flex gap-2 items-center text-gray-800 text-base font-normal leading-[130%] tracking-[-0.32px]">
          <Icon path={mdiDoor} size="1.25rem" className="text-gray-800 shrink-0" />
          <span>{formatRoomDisplay(roomName, t)}</span>
        </div>
        <div className="flex gap-2 items-center text-gray-800 text-base font-normal leading-[130%] tracking-[-0.32px]">
          <Icon path={mdiClockOutline} size="1.25rem" className="text-gray-800 shrink-0" />
          <span>
            {formatTimeRangeInLA(startTime, endTime)} {t("timezoneLosAngeles")}
          </span>
        </div>
        <div className="flex gap-2 items-center text-gray-800 text-base font-normal leading-[130%] tracking-[-0.32px]">
          <Icon path={mdiCurrencyUsd} size="1.25rem" className="text-gray-800 shrink-0" />
          <span>
            {t("reservationHours", { count: hours })} ({paymentLabel})
          </span>
        </div>
      </div>
    </div>
  );
}
